/**
 * Reset-zoom policy per timeframe class: what a chart shows after the body
 * double-click / context-menu "Reset zoom" gesture, chosen once per user and
 * honored by every chart. Absent rows keep today's fixed reset (default bar
 * space, scrolled to the latest bar).
 *
 * Spec: docs/superpowers/specs/2026-09-22-chart-reset-zoom-defaults-design.md
 * (D3 category map, D4 levels, D5 intraday anchors, D6 range modes, D7 caps,
 * D8 storage shape).
 */

import type { Period } from '../utils/resolution'

/** The four settings rows (spec D3). Deliberately its own type, not the
 *  indicator drawer's `TimeframeCategory`, which has no weekly/monthly row. */
export type ZoomCategory = 'intraday' | 'hourly' | 'daily' | 'weeklyMonthly'

/** Zoom levels are multipliers of the chart's default bar space (spec D4). */
export type ZoomLevel = 'in' | 'standard' | 'out'

/** Where an intraday range starts, as a session point (spec D5). */
export type IntradayAnchor = 'open' | 'premarket' | 'yesterdayAfterHours'

export type ResetZoomRule =
  | { kind: 'level'; level: ZoomLevel }
  /** Intraday only: from the anchor to the latest bar, or at least `minBars`. */
  | { kind: 'anchor'; anchor: IntradayAnchor; minBars: number }
  /** Hourly and daily: the last `days` trading sessions. */
  | { kind: 'daysBack'; days: number }
  /** Weekly and monthly: the last `bars` bars. */
  | { kind: 'barsBack'; bars: number }

export type ResetZoomPolicy = Partial<Record<ZoomCategory, ResetZoomRule>>

/** A range-kind rule (everything but a zoom level). */
export type ResetZoomRangeRule = Exclude<ResetZoomRule, { kind: 'level' }>

export const ZOOM_CATEGORIES: readonly ZoomCategory[] = [
  'intraday',
  'hourly',
  'daily',
  'weeklyMonthly',
]

export const ZOOM_LEVELS: readonly ZoomLevel[] = ['in', 'standard', 'out']

export const INTRADAY_ANCHORS: readonly IntradayAnchor[] = [
  'open',
  'premarket',
  'yesterdayAfterHours',
]

/** Multipliers of `defaultBarSpace`, not literal pixels, so a change to the
 *  base propagates (spec D4). Standard is byte-identical to today's reset. */
export const ZOOM_LEVEL_MULTIPLIER: Record<ZoomLevel, number> = { in: 2, standard: 1, out: 0.5 }

interface ResetZoomCap {
  /** The one range kind this category allows. */
  kind: ResetZoomRangeRule['kind']
  /** The numeric field on that rule. */
  field: 'minBars' | 'days' | 'bars'
  /** Value written when the user first picks the range option. */
  default: number
  /** Hard cap; chosen so the range resolves inside one first-page fetch (D7). */
  max: number
  /** Quick-pick values for the number row. */
  presets: readonly number[]
}

/**
 * Defaults, caps and presets per row (spec D7). Every cap is pinned by
 * `resetZoom.unit.test.ts` against `pageMsFromBars`, so a change to the
 * fetch constants moves the cap, not the test.
 */
export const RESET_ZOOM_CAPS = {
  intraday: {
    kind: 'anchor',
    field: 'minBars',
    default: 100,
    max: 200,
    presets: [50, 100, 150, 200],
  },
  hourly: { kind: 'daysBack', field: 'days', default: 10, max: 50, presets: [5, 10, 20, 50] },
  daily: { kind: 'daysBack', field: 'days', default: 90, max: 365, presets: [20, 60, 120, 250] },
  weeklyMonthly: {
    kind: 'barsBack',
    field: 'bars',
    default: 100,
    max: 520,
    presets: [52, 104, 260, 520],
  },
} as const satisfies Record<ZoomCategory, ResetZoomCap>

/**
 * Four-way map for THIS feature. Deliberately not timeframeVisibility's
 * CATEGORY_OF_UNIT: that one folds 1W/1M under Daily for the indicator
 * Visibility tab, and changing it would move those checkboxes (spec D3).
 */
export function zoomCategoryOf(period: Period): ZoomCategory {
  switch (period.timespan) {
    case 'minute':
      return 'intraday'
    case 'hour':
      return 'hourly'
    case 'day':
      return 'daily'
    default:
      // week, month (and a hypothetical year) share the bars-back row.
      return 'weeklyMonthly'
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Round to an integer and clamp to [1, max]; undefined for a non-finite input. */
function clampCount(value: unknown, max: number): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined
  return Math.min(max, Math.max(1, Math.round(value)))
}

/**
 * Drop a rule whose kind is not allowed for the category; clamp its number
 * to [1, cap]; coerce non-integers. Returns undefined for anything unusable.
 * The result is a fresh object carrying only the rule's own fields, so junk
 * keys from a stored value never survive a round trip.
 */
export function clampResetZoomRule(
  category: ZoomCategory,
  rule: unknown,
): ResetZoomRule | undefined {
  if (!isRecord(rule)) return undefined
  const cap = RESET_ZOOM_CAPS[category]

  if (rule.kind === 'level') {
    const level = rule.level
    return typeof level === 'string' && (ZOOM_LEVELS as readonly string[]).includes(level)
      ? { kind: 'level', level: level as ZoomLevel }
      : undefined
  }
  if (rule.kind !== cap.kind) return undefined

  switch (cap.kind) {
    case 'anchor': {
      const anchor = rule.anchor
      if (typeof anchor !== 'string' || !(INTRADAY_ANCHORS as readonly string[]).includes(anchor)) {
        return undefined
      }
      const minBars = clampCount(rule.minBars, cap.max)
      return minBars === undefined
        ? undefined
        : { kind: 'anchor', anchor: anchor as IntradayAnchor, minBars }
    }
    case 'daysBack': {
      const days = clampCount(rule.days, cap.max)
      return days === undefined ? undefined : { kind: 'daysBack', days }
    }
    case 'barsBack': {
      const bars = clampCount(rule.bars, cap.max)
      return bars === undefined ? undefined : { kind: 'barsBack', bars }
    }
  }
}

/**
 * Sanitize a stored / server policy: unknown category keys are dropped and
 * each known one runs through {@link clampResetZoomRule}. Returns undefined
 * for a non-object and when no rule survives, so a consumer can treat the
 * result as "absent" with a single truthiness check.
 */
export function sanitizeResetZoomPolicy(raw: unknown): ResetZoomPolicy | undefined {
  if (!isRecord(raw)) return undefined
  const policy: ResetZoomPolicy = {}
  let hasAny = false
  for (const category of ZOOM_CATEGORIES) {
    const rule = clampResetZoomRule(category, raw[category])
    if (rule) {
      policy[category] = rule
      hasAny = true
    }
  }
  return hasAny ? policy : undefined
}
